
import { useState } from 'react';

export default function EventOverview() {
  const [showTooltip, setShowTooltip] = useState(false);

  const principles = [
    {
      icon: 'ri-compass-3-line',
      title: '자발적 참여',
      description: '락업 수량과 기간(블록 수)을 100% 자율적으로 선택'
    },
    {
      icon: 'ri-anchor-line',
      title: '투명성',
      description: '모든 락업 내역은 실시간으로 공개되며, 순위표를 통해 확인할 수 있습니다.'
    },
    {
      icon: 'ri-ship-2-line',
      title: '실체 있는 신뢰',
      description: '온라인의 약속은 수원모빅회관에서의 만남을 통해 완성됩니다.'
    },
    {
      icon: 'ri-scales-3-line',
      title: '균형잡힌 보상',
      description: '단기 이익이 아닌 장기적 가치 창출을 위한 여정에 동참하세요.',
      hasTooltip: true
    }
  ];

  return (
    <section id="event" className="bg-[#0A1628] py-10 sm:py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Title */}
        <div className="text-center mb-6 sm:mb-16 lg:mb-20">
          <h2 className="text-2xl sm:text-4xl lg:text-6xl font-bold text-white mb-2 sm:mb-6">
            정박 과 항해
          </h2>
          <div className="flex justify-center">
            <div className="w-12 sm:w-24 lg:w-32 h-0.5 bg-[#D4AF37]"></div>
          </div>
          <p className="text-sm sm:text-lg lg:text-xl text-white/80 mt-3 sm:mt-8 max-w-3xl mx-auto leading-relaxed px-2">
            '뉴베드포드'의 시작을 셀프 락업 커밋먼트로 함께 선언하세요.
          </p>
        </div>

        {/* Principles Cards - 모바일 2열 */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {principles.map((principle, index) => (
            <div
              key={index}
              className="bg-[#1A2332] rounded-xl sm:rounded-2xl p-4 sm:p-8 hover:-translate-y-2 transition-all duration-300 hover:shadow-2xl group cursor-pointer relative"
            >
              <div className="flex flex-col items-start gap-3 sm:gap-6">
                <div className="flex items-center gap-2 w-full">
                  <div className="w-10 h-10 sm:w-14 lg:w-16 sm:h-14 lg:h-16 rounded-full bg-[#D4AF37] bg-opacity-10 flex items-center justify-center group-hover:bg-[#D4AF37] group-hover:bg-opacity-20 transition-colors flex-shrink-0">
                    <i className={`${principle.icon} text-[#D4AF37] text-lg sm:text-3xl`}></i>
                  </div>
                  {principle.hasTooltip && (
                    <div className="relative z-10">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowTooltip(!showTooltip);
                        }}
                        className="min-w-[36px] min-h-[36px] sm:min-w-[44px] sm:min-h-[44px] rounded-full bg-gradient-to-r from-[#D4AF37] to-[#B8941F] flex items-center justify-center hover:shadow-xl hover:shadow-[#D4AF37]/50 transition-all duration-300 hover:scale-110 cursor-pointer animate-pulse"
                        aria-label="보상 산식 설명"
                      >
                        <i className="ri-question-line text-white text-base sm:text-xl font-bold"></i>
                      </button>
                      <div className="absolute inset-0 rounded-full border-2 border-[#D4AF37] animate-ping opacity-75 pointer-events-none"></div>
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="text-sm sm:text-2xl font-bold text-white mb-1.5 sm:mb-3">
                    {principle.title}
                  </h3>
                  <p className="text-[#708090] text-xs sm:text-lg leading-relaxed">
                    {principle.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tooltip - 모바일 최적화 */}
        {showTooltip && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/60"
            onClick={() => setShowTooltip(false)}
          >
            <div
              className="bg-[#0F1A2A] border-t sm:border border-[#D4AF37]/30 rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 shadow-2xl w-full sm:max-w-3xl max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h4 className="text-white font-bold text-base sm:text-xl">💡 보상 산식</h4>
                <button
                  onClick={() => setShowTooltip(false)}
                  className="min-w-[44px] min-h-[44px] rounded-full bg-[#D4AF37]/20 flex items-center justify-center hover:bg-[#D4AF37]/30 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-[#D4AF37] text-xl"></i>
                </button>
              </div>

              {/* Formula Image */}
              <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-6 lg:p-8 mb-4 sm:mb-6 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/227cab682d83965c949f1d2451ee4e6c/6a234fd42af2690adc192f31fb4e1e7b.png"
                  alt="Final Score Formula"
                  className="max-w-full h-auto"
                />
              </div>

              {/* Explanation */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="bg-[#1A2332]/50 rounded-lg p-3.5 sm:p-5">
                  <div className="flex items-start gap-2 sm:gap-3 mb-2">
                    <span className="text-[#D4AF37] text-xl sm:text-2xl whitespace-nowrap">⚙️</span>
                    <h5 className="text-white font-bold text-sm sm:text-lg">정밀 보정 (0.48승)</h5>
                  </div>
                  <p className="text-white/80 text-xs sm:text-base leading-relaxed">
                    단순 제곱근(0.5)보다 고도화된 0.48지수를 적용했습니다. 이는 특정 고래의 점수 독식을 방지함과 동시에,
                    100일과 150일 등 장기 락업자 간의 미세한 차이를 소수점 끝자리까지 공정하게 변별하여 반영합니다.
                  </p>
                </div>

                <div className="bg-[#1A2332]/50 rounded-lg p-3.5 sm:p-5">
                  <div className="flex items-start gap-2 sm:gap-3 mb-2">
                    <span className="text-[#D4AF37] text-xl sm:text-2xl whitespace-nowrap">⚖️</span>
                    <h5 className="text-white font-bold text-sm sm:text-lg">황금 비율</h5>
                  </div>
                  <p className="text-white/80 text-xs sm:text-base leading-relaxed">
                    1 MO당 30일(1개월) 예치를 표준 황금 비율로 삼습니다. 수량과 기간이 완벽한 균형을 이룰수록
                    가중치가 극대화되며, 진정한 장기 홀더가 정당한 순위를 보장받을 수 있도록 설계된 신념 증명 시스템입니다.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
